/* eslint-disable max-lines */
/* eslint-disable import/prefer-default-export */
import findKey from "lodash/findKey";
import { MetaTag } from "next-seo/lib/types";

import { getBasePath } from "./url";
import { locales, routing } from "@/i18n/routing";

const RTL_LOCALES: string[] = [];
const LOCALE_NAME = {
  en: "English",
  tr: "Turkish",
};

const LOCALE_NAME_TO_CODE = {
  english: "en",
  turkish: "tr",
};

export const LANG_LOCALE_MAP = {
  en: "en-US",
  tr: "tr-TR",
};

export enum Direction {
  LTR = "ltr",
  RTL = "rtl",
}

export const Languages = {
  0: {
    // English
    locale: "en",
  },
  1: {
    // Turkish
    locale: "tr",
  },
};

interface LinkLanguageAlternate {
  hrefLang: string;
  href: string;
}

interface LanguageData {
  direction: string;
  font: string;
  code: string;
}

/**
 * Check whether the current locale is RTL.
 *
 * @param {string} locale
 * @returns {boolean}
 */
export const isRTLLocale = (locale: string): boolean => {
  return RTL_LOCALES.includes(locale);
};

/**
 * Gir the dir of the element based on the locale.
 *
 * @param {string} locale
 * @returns {string}
 */
export const getDir = (locale: string): Direction =>
  isRTLLocale(locale) ? Direction.RTL : Direction.LTR;

/**
 * Get direction and font name of language by language id
 *
 * @param {number} languageId
 * @returns {LanguageData}
 */
export const getLanguageDataById = (languageId: number): LanguageData => {
  const lang = Languages[languageId];

  return {
    font: lang?.font,
    direction: lang?.dir || Direction.LTR,
    code: lang?.locale || "ar",
  };
};

/**
 * Get direction of language by language id
 *
 * @param {number} languageId
 * @returns {string}
 */
export const getLanguageDirectionById = (languageId: number): string => {
  const mapping = Languages[languageId];

  return mapping?.dir || Direction.LTR;
};

/**
 * Get font face name of language by language id
 *
 * @param {number} languageId
 * @returns {string}
 */
export const getLanguageFontById = (languageId: number): string => {
  const mapping = Languages[languageId];
  return mapping?.font;
};

/**
 * Find language Id by its locale
 *
 * @param {string} locale
 * @returns {number} language id
 */
export const findLanguageIdByLocale = (locale: string): number => {
  return Number(findKey(Languages, { locale }));
};

/**
 * Generate the language alternates of a given path so that Search Engines can
 * recommend the alternate page to the users based on their region/locale.
 *
 * @see https://developers.google.com/search/docs/advanced/crawling/localized-versions
 * @param {string} path
 * @returns {LinkLanguageAlternate[]}
 */
export const getLanguageAlternates = (
  path: string
): LinkLanguageAlternate[] => {
  const { locales } = routing;
  const basePath = getBasePath();
  return locales
    .map((locale) => ({
      hrefLang: locale,
      href: `${basePath}/${locale}${path === "/" ? "" : path}`,
    }))
    .concat({
      hrefLang: "x-default" as any, // used when no other language/region matches the user's browser setting @see https://developers.google.com/search/docs/advanced/crawling/localized-versions#xdefault
      href: `${basePath}${path}`,
    });
};

/**
 * Get the locale name.
 *
 * @param {string} locale
 * @returns {string}
 */
export const getLocaleName = (locale: string): string => LOCALE_NAME[locale];

/**
 * Converts a locale name e.g. 'english' to its code e.g. 'en'.
 *
 * @param {string} fullName
 * @returns {string}
 */
export const getLocaleNameByFullName = (fullName: string): string =>
  LOCALE_NAME[LOCALE_NAME_TO_CODE[fullName]];

/**
 * Takes a number and returns a localized string based on the provided locale.
 *
 * @param {number} value
 * @param {string} locale
 * @param {boolean} showLeadingZero
 * @param {Intl.NumberFormatOptions} options
 * @returns {string}
 */
// Intl.NumberFormat is performance heavy so we are caching the formatter.
const numberFormatters: Map<
  Intl.NumberFormatOptions | string,
  Intl.NumberFormat
> = new Map();
let currentLanguageLocale: string | null = null;

export const toLocalizedNumber = (
  value: number,
  locale: string,
  showLeadingZero = false,
  options: Intl.NumberFormatOptions | undefined = undefined
) => {
  // we do this because an empty object will result in a new formatter being created everytime since we don't have it's reference.
  const formatterKey = options ?? "DEFAULT_OPTIONS";

  if (numberFormatters.has(formatterKey) && currentLanguageLocale === locale) {
    return getFormattedNumber(
      numberFormatters.get(formatterKey)!,
      value,
      showLeadingZero
    );
  }

  currentLanguageLocale = locale;
  const fullLocale = LANG_LOCALE_MAP[locale];

  const newNumberFormatter = new Intl.NumberFormat(fullLocale, options);
  numberFormatters.set(formatterKey, newNumberFormatter);
  return getFormattedNumber(newNumberFormatter, value, showLeadingZero);
};

/**
 * Get the formatted localized number. This either returns
 * the original value or prepends a leading 0 to the beginning
 * of the string if it's allowed and the value is below 10.
 *
 * @param {Intl.NumberFormat} formatter
 * @param {number} value
 * @param {boolean} showLeadingZero
 * @returns {string}
 */
const getFormattedNumber = (
  formatter: Intl.NumberFormat,
  value: number,
  showLeadingZero: boolean
): string => {
  const formattedNumber = formatter.format(value);
  if (!showLeadingZero || value >= 10) {
    return formattedNumber;
  }
  return `${formatter.format(0)}${formattedNumber}`;
};

/**
 * Get the full locale name with lang + country e.g. ar-SA or en-US.
 *
 * @param {string} locale
 * @returns {string}
 */
export const getLangFullLocale = (locale: string): string =>
  LANG_LOCALE_MAP[locale];

/**
 * Takes a date and returns a localized string based on the provided locale and options.
 *
 * @param {number} value
 * @param {string} locale
 * @param {Intl.DateTimeFormatOptions} options
 * @returns {string}
 */
export const toLocalizedDate = (
  value: number | Date,
  locale: string,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  const fullLocale = LANG_LOCALE_MAP[locale];
  return new Intl.DateTimeFormat(fullLocale, options).format(value);
};

/**
 * Generate the alternate locales that the current content can be served in.
 * When Facebook needs to render an object in one of the specified locales,
 * it will make a request to the URL with the fb_locale URL parameter.
 *
 * @see https://developers.facebook.com/blog/post/2013/11/11/605/
 *
 * @param {string} currentLocale
 * @returns {MetaTag[]}
 */
export const getOpenGraphAlternateLocales = (
  currentLocale: string
): MetaTag[] => {
  return Object.keys(LANG_LOCALE_MAP)
    .filter((languageCode) => languageCode !== currentLocale)
    .map((languageCode) => ({
      name: "og:locale:alternate",
      content: `${LANG_LOCALE_MAP[languageCode]}`.replace("-", "_"),
    }));
};

/**
 * Convert a month number to the corresponding localized month name.
 *
 * @param {number} monthNumber
 * @param {string} locale
 * @returns {string}
 */
export const toLocalizedMonthName = (
  monthNumber: number,
  locale: string
): string => {
  const objDate = new Date();
  objDate.setDate(1);
  objDate.setMonth(monthNumber - 1);

  return objDate.toLocaleString(locale, { month: "long" });
};

/**
 * Extracts the current locale from the URL path.
 */
export function getCurrentLocale(defaultLocale = "tr"): string {
  if (typeof window === "undefined") return defaultLocale;

  const pathParts = window.location.pathname.split("/");
  const locale = pathParts[1];

  return locales.includes(locale as any) ? locale : defaultLocale;
}
